import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Building2, 
  User, 
  Mail, 
  Phone,
  Briefcase,
  CheckCircle2,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Shield
} from 'lucide-react';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_SIZES = [
  { value: 'small', label: 'Small Business' },
  { value: 'wosb', label: 'Women-Owned Small Business (WOSB)' },
  { value: '8a', label: 'SBA 8(a)' },
  { value: 'hubzone', label: 'HUBZone' },
  { value: 'sdvosb', label: 'Service-Disabled Veteran-Owned (SDVOSB)' },
  { value: 'large', label: 'Large Business' },
];

const INDUSTRY_SECTORS = [
  { value: 'defense', label: 'Defense & Aerospace' },
  { value: 'it', label: 'Information Technology' },
  { value: 'healthcare', label: 'Healthcare & Life Sciences' },
  { value: 'energy', label: 'Energy & Environment' },
  { value: 'logistics', label: 'Logistics & Transportation' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'construction', label: 'Construction & Engineering' },
  { value: 'other', label: 'Other' },
];

const PLANS = {
  SMALL_BUSINESS: { name: 'Small Business', price: '$250/mo', pursuits: 2, users: 3 },
  GROWTH: { name: 'Growth', price: '$950/mo', pursuits: 10, users: 10 },
  ENTERPRISE: { name: 'Enterprise', price: 'Custom', pursuits: 'Unlimited', users: 'Unlimited' },
  // Consultant plans
  INDEPENDENT: { name: 'Independent', price: '$149/mo', pursuits: 'N/A', users: 'N/A', clients: 2 },
  AGENCY: { name: 'Agency', price: '$499/mo', pursuits: 'N/A', users: 'N/A', clients: 5, associates: 3 },
};

// Personal email domains that are blocked for corporate registration
const BLOCKED_PERSONAL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'live.com', 'msn.com', 'me.com', 'mac.com', 'comcast.net', 'att.net',
  'verizon.net', 'sbcglobal.net', 'bellsouth.net', 'cox.net', 'charter.net',
  'earthlink.net', 'juno.com', 'netzero.net', 'aim.com', 'gmx.com',
  'inbox.com', 'fastmail.com', 'hushmail.com', 'tutanota.com',
];

// Check if email is from a corporate domain
const isCorporateEmail = (email) => {
  if (!email || !email.includes('@')) return false;
  const domain = email.toLowerCase().split('@')[1];
  return !BLOCKED_PERSONAL_DOMAINS.includes(domain);
};

// Get email domain for display
const getEmailDomain = (email) => {
  if (!email || !email.includes('@')) return '';
  return email.toLowerCase().split('@')[1];
};

export default function TrialRequestModal({ isOpen, onClose, selectedPlan = 'GROWTH', requestType = 'CONTRACTOR' }) {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  
  const isConsultant = requestType === 'CONSULTANCY';
  const planInfo = PLANS[selectedPlan] || PLANS.GROWTH;
  
  const [formData, setFormData] = useState({
    companyName: '',
    uei: '',
    cage: '',
    businessSize: '',
    industrySector: '',
    website: '',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    contactTitle: '',
    message: '',
    requestedPlan: selectedPlan,
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    
    // Soft hint for personal-email domains. We no longer BLOCK the
    // submission — early-stage founders frequently use Gmail before they
    // own a corporate domain. Backend tags the row `is_personal_email:true`
    // so admins see a warning chip on the lifecycle dashboard.
    if (field === 'contactEmail') {
      if (value && value.includes('@') && !isCorporateEmail(value)) {
        const domain = getEmailDomain(value);
        setEmailError(`Heads up — we recommend a corporate email if you have one. ${domain} addresses go through manual admin review.`);
      } else {
        setEmailError(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/trial/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_type: requestType,  // CONTRACTOR or CONSULTANCY
          company_name: formData.companyName,
          uei: formData.uei || null,
          cage_code: formData.cage || null,
          business_size: formData.businessSize,
          industry_sector: formData.industrySector,
          website: formData.website || null,
          contact_first_name: formData.contactFirstName,
          contact_last_name: formData.contactLastName,
          contact_email: formData.contactEmail,
          contact_phone: formData.contactPhone || null,
          contact_title: formData.contactTitle || null,
          message: formData.message || null,
          requested_plan: formData.requestedPlan,
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit trial request');
      }
      
      setIsSubmitted(true);
    } catch (err) {
      console.error('Trial request error:', err);
      setError(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const canProceedStep1 = formData.companyName && formData.businessSize && formData.industrySector;
  // Submit gate: just require the contact fields and a syntactically
  // valid email. Personal-email submissions are accepted with a soft
  // warning (see `updateFormData`).
  const canSubmit = formData.contactFirstName && formData.contactLastName &&
                    formData.contactEmail && formData.contactEmail.includes('@');

  const handleClose = () => {
    setStep(1);
    setIsSubmitted(false);
    setError(null);
    setEmailError(null);
    setFormData({
      companyName: '',
      uei: '',
      cage: '',
      businessSize: '',
      industrySector: '',
      website: '',
      contactFirstName: '',
      contactLastName: '',
      contactEmail: '',
      contactPhone: '',
      contactTitle: '',
      message: '',
      requestedPlan: selectedPlan,
    });
    onClose();
  };

  if (isSubmitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg" data-testid="trial-request-success-modal">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Request Submitted!
            </h2>
            <p className="text-slate-600 mb-4">
              Thank you for your interest in FedBD. We'll review your request and get back to you within 24 hours.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-slate-500 mb-2">What happens next?</p>
              <ol className="text-sm text-slate-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="bg-teal-100 text-teal-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">1</span>
                  <span>Our team reviews your application</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-teal-100 text-teal-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">2</span>
                  <span>You'll receive an email invitation to activate your trial</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-teal-100 text-teal-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">3</span>
                  <span>Set up your account and start your 7-day free trial</span>
                </li>
              </ol>
            </div>
            <Button 
              className="w-full bg-teal-700 hover:bg-teal-800 text-white"
              onClick={handleClose}
              data-testid="close-success-modal-btn"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="trial-request-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {step === 1 ? 'Organization Details' : 'Contact Information'}
            </DialogTitle>
            <Badge className="bg-teal-100 text-teal-700">
              <Sparkles className="w-3 h-3 mr-1" />
              {planInfo.name} Plan Trial
            </Badge>
          </div>
          <DialogDescription>
            {step === 1 
              ? 'Tell us about your organization to start your 7-day free trial'
              : 'We\'ll use this information to set up your account'
            }
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center justify-center mb-4">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${step >= 1 ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
            1
          </div>
          <div className={`w-16 h-1 mx-2 rounded ${step > 1 ? 'bg-teal-600' : 'bg-slate-200'}`} />
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${step >= 2 ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
            2
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="companyName"
                    placeholder="Acme Defense Systems, LLC"
                    value={formData.companyName}
                    onChange={(e) => updateFormData('companyName', e.target.value)}
                    className="pl-10"
                    data-testid="trial-company-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="uei">UEI (Unique Entity ID)</Label>
                  <Input
                    id="uei"
                    placeholder="e.g., H93L94K2M1P5"
                    value={formData.uei}
                    onChange={(e) => updateFormData('uei', e.target.value.toUpperCase())}
                    className="font-mono"
                    maxLength={12}
                    data-testid="trial-uei"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cage">CAGE Code</Label>
                  <Input
                    id="cage"
                    placeholder="e.g., 7Z9X2"
                    value={formData.cage}
                    onChange={(e) => updateFormData('cage', e.target.value.toUpperCase())}
                    className="font-mono"
                    maxLength={5}
                    data-testid="trial-cage"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Size/Set-Aside *</Label>
                  <Select value={formData.businessSize} onValueChange={(v) => updateFormData('businessSize', v)}>
                    <SelectTrigger data-testid="trial-business-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_SIZES.map((size) => (
                        <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Industry Sector *</Label>
                  <Select value={formData.industrySector} onValueChange={(v) => updateFormData('industrySector', v)}>
                    <SelectTrigger data-testid="trial-industry-sector">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_SECTORS.map((sector) => (
                        <SelectItem key={sector.value} value={sector.value}>{sector.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Corporate Website</Label>
                <Input
                  id="website"
                  placeholder="https://www.yourcompany.com"
                  value={formData.website}
                  onChange={(e) => updateFormData('website', e.target.value)}
                  data-testid="trial-website"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-700">
                    This person will be the primary admin with full access to your workspace after trial approval.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contactFirstName">First Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="contactFirstName"
                      placeholder="John"
                      value={formData.contactFirstName}
                      onChange={(e) => updateFormData('contactFirstName', e.target.value)}
                      className="pl-10"
                      data-testid="trial-first-name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactLastName">Last Name *</Label>
                  <Input
                    id="contactLastName"
                    placeholder="Smith"
                    value={formData.contactLastName}
                    onChange={(e) => updateFormData('contactLastName', e.target.value)}
                    data-testid="trial-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">Work Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="contactEmail"
                    type="email"
                    placeholder="john@yourcompany.com"
                    value={formData.contactEmail}
                    onChange={(e) => updateFormData('contactEmail', e.target.value)}
                    className={`pl-10 ${emailError ? 'border-amber-300 focus:ring-amber-500' : ''}`}
                    data-testid="trial-email"
                  />
                </div>
                {emailError && (
                  <p className="text-xs text-amber-700 mt-1">{emailError}</p>
                )}
                <p className="text-xs text-slate-500">
                  Corporate email required (e.g., @yourcompany.com)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contactPhone">Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="contactPhone"
                      placeholder="+1 (555) 123-4567"
                      value={formData.contactPhone}
                      onChange={(e) => updateFormData('contactPhone', e.target.value)}
                      className="pl-10"
                      data-testid="trial-phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactTitle">Job Title</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="contactTitle"
                      placeholder="VP of Business Development"
                      value={formData.contactTitle}
                      onChange={(e) => updateFormData('contactTitle', e.target.value)}
                      className="pl-10"
                      data-testid="trial-title"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Tell us about your capture needs (optional)</Label>
                <Textarea
                  id="message"
                  placeholder="What challenges are you looking to solve with AI-powered capture management?"
                  value={formData.message}
                  onChange={(e) => updateFormData('message', e.target.value)}
                  rows={3}
                  data-testid="trial-message"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mt-4">
              {error}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            {step > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(step - 1)}
                data-testid="trial-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}
            
            {step < 2 ? (
              <Button
                type="button"
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="bg-teal-700 hover:bg-teal-800 text-white"
                data-testid="trial-next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={!canSubmit || isLoading}
                className="bg-teal-700 hover:bg-teal-800 text-white"
                data-testid="trial-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Request Trial
                    <CheckCircle2 className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
